import React, { useCallback } from 'react';
import { useAsyncValue, useNavigate, useRevalidator, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import debug from 'debug';

// Styles
import { Container, ContainerButton } from '../../components/MainStyledComponents';

//Components
import { MainButton } from '../../../../../components/inputs/buttons/MainButton';

// Types
import { MultimediaCheckBoxTable } from '../../../../../components/display/tables/MultimediaCheckBoxTable';
import {
    tableIds,
    takeBase,
} from '../../../../../components/display/tables/types';
import { firstPageTake } from '../../../../../utils/cursor-take.helper';

import { dataTypes } from '../../../../../components/display/tables/MultimediaCheckBoxTable/types';

// Redux
import { getCursor } from '../../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../../state/actions/ui/cursorTables';
import { openModal } from '../../../../../state/actions/ui/modal';
import { store } from '../../../../../state/configureStore';
import { modalIds } from '../../../../../components/modals/SuperModal/types';
import { roidOTSelected } from '../../../../../state/actions/ui/roidOTSelectedGeneralView';
import { OrderType } from '../../../../../types/orders';

// Hooks
import { usePersistentSelection } from '../../../../../hooks/usePersistentSelection';

const log = debug('rois:order-multimedia-page');

if (import.meta.env.DEV) {
    debug.enable('rois:order-multimedia-page*');
}

export const OrderMultimediaPage = () => {
    const { t } = useTranslation(['pages', 'workOrders']);
    const data = useAsyncValue() as dataTypes;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const revalidator = useRevalidator();
    const { id } = useParams();

    const {
        rowSelection,
        onRowSelectionChange,
        selectedCount,
        selectAllMode,
        excludedIds,
        selectedRows,
        handleSelectAll,
        handleClearSelection,
    } = usePersistentSelection({
        dataAll: data?.dataOrderWithMultimedia ?? [],
        totalCount: data?.totalCount ?? 0,
    });

    React.useLayoutEffect(() => {
        if (data.parentOrderRoid && id) {
            dispatch(
                roidOTSelected(
                    data.parentOrderType === OrderType.Campaign
                        ? OrderType.Campaign
                        : OrderType.Generic,
                    Array.isArray(data.parentOrderRoid)
                        ? data.parentOrderRoid.join('.')
                        : data.parentOrderRoid,
                    id
                )
            );
        }
    }, [data.parentOrderRoid, data.parentOrderType, id, dispatch]);

    const handleSeeOrder = useCallback(() => {
        if (selectedRows[0]) {
            navigate(`/work-orders/multimedia/${selectedRows[0]}`);
        }
    }, [selectedRows, navigate]);

    const handleDownloadPhotos = useCallback(() => {
        const currentCursor = getCursor(tableIds.MULTIMEDIA)(store.getState());
        // Drop `id` (the tableId — 'multimedia'), `take`, `cursor`: none are valid
        // FilterOrderList fields. The remaining keys are the actual filters the
        // backend expects (state, type, fromDate, concessionId, …).
        const { id: _id, take: _take, cursor: _cursor, ...filterFields } = currentCursor || ({} as any);
        dispatch(
            openModal(modalIds.DOWNLOAD_MULTIMEDIA_PHOTOS, {
                selectedRows: selectAllMode ? [] : selectedRows,
                selectAll: selectAllMode,
                excludedIds: selectAllMode ? [...excludedIds] : [],
                filters: filterFields,
                totalCount: data?.totalCount ?? 0,
            })
        );
    }, [
        selectedRows,
        dispatch,
        selectAllMode,
        excludedIds,
        data?.totalCount,
    ]);

    const onReFetchData = useCallback(async () => {
        const currentCursor = getCursor(tableIds.MULTIMEDIA)(store.getState());
        log('🔄 onReFetchData - currentCursor:', currentCursor);
        // Tarea #11: respetar el take guardado en cursor (page-size selector).
        const effectiveTake = firstPageTake(currentCursor.take, takeBase);
        const setCursorData = {
            take: effectiveTake,
            isMultimedia: true,
            orderBy: currentCursor?.orderBy ?? 'desc',
            sortField: currentCursor?.sortField,
            cursor: '',
            states: currentCursor?.states,
            type: currentCursor?.type,
            fromDate: currentCursor?.fromDate,
            toDate: currentCursor?.toDate,
            indexType: currentCursor?.indexType,
            indexExpedition: currentCursor?.indexExpedition,
            classification: currentCursor?.classification,
            predefinedOrderId: currentCursor?.predefinedOrderId,
            roadId: currentCursor.roadId,
            concessionId: currentCursor?.concessionId,
            capitolId: currentCursor?.capitolId,
            subCapitolId: currentCursor?.subCapitolId,
            userIds: currentCursor?.userIds,
            pkInitKm: currentCursor?.pkInitKm,
            pkInitMeter: currentCursor?.pkInitMeter,
            pkEndKm: currentCursor?.pkEndKm,
            pkEndMeter: currentCursor?.pkEndMeter,
        };

        log('🔄 onReFetchData - setCursorData (isMultimedia=%s):', setCursorData.isMultimedia, setCursorData);
        dispatch(setCursor(tableIds.MULTIMEDIA, setCursorData));

        revalidator.revalidate();
    }, []);

    const onRefetchPagination = useCallback(async () => {
        revalidator.revalidate();
    }, [revalidator]);

    return (
        <Container>
            <MultimediaCheckBoxTable
                idCss="order-multimedia"
                titleText={t('pages:workOrders.multimedia')}
                data={data}
                onReFetchData={onReFetchData}
                onReFetchPagination={onRefetchPagination}
                tableId={tableIds.MULTIMEDIA}
                selectionMode="checkbox"
                rowSelection={rowSelection}
                onRowSelectionChange={onRowSelectionChange}
                selectAllMode={selectAllMode}
                excludedIds={excludedIds}
                onSelectAll={handleSelectAll}
                onClearSelection={handleClearSelection}
                selectedCount={selectedCount}
            />
            <ContainerButton>
                <MainButton
                    variant="secondary"
                    text={t('pages:workOrders.viewPhotos', 'Ver fotos')}
                    disabled={selectedCount !== 1}
                    onClick={handleSeeOrder}
                />
                <MainButton
                    text={t('pages:workOrders.downloadPhotos')}
                    disabled={selectedCount === 0}
                    onClick={handleDownloadPhotos}
                />
            </ContainerButton>
        </Container>
    );
};

export default OrderMultimediaPage;
